<template>
  <!-- Printer Config Button -->
  <div class="printer-config-button">
    <button @click="showPrinterConfig = !showPrinterConfig" class="btn btn-sm btn-outline-secondary">
      <i class="fas fa-print"></i> Printer
    </button>
  </div>
  <div class="welcome-screen">
    <!-- Printer Config Panel -->
    <div v-if="showPrinterConfig" class="printer-config-panel">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Printer Configuration</h5>
          <button @click="showPrinterConfig = false" class="btn btn-sm btn-close"></button>
        </div>
        <div class="card-body">
          <div class="mb-3">
            <label for="kioskIdSelect" class="form-label">Kiosk ID:</label>
            <select class="form-select" id="kioskIdSelect" v-model="kioskId">
              <option value="">Select Kiosk ID</option>
              <option value="kiosk-1">Kiosk 1</option>
              <option value="kiosk-2">Kiosk 2</option>
            </select>
            <small class="form-text text-muted">
              This will determine which printer to use
            </small>
          </div>
          
          <div class="mb-3">
            <label class="form-label">Current Configuration:</label>
            <div class="alert alert-info">
              <strong>Kiosk ID:</strong> {{ currentKioskId || 'Not set' }}<br>
              <strong>Printer:</strong> {{ currentPrinterName || 'Default' }}
            </div>
          </div>
          
          <button @click="saveKioskConfig" class="btn btn-primary">Save Configuration</button>
        </div>
      </div>
    </div>
    
    <div class="welcome-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading kiosk settings...</p>
      </div>
      
      <!-- Main Content (shown after loading, even on API error) -->
      <template v-else>
        <h1 class="welcome-title">{{ kioskTitle }}</h1>
        <p class="welcome-message">{{ welcomeMessage }}</p>
        
        <div class="welcome-cta">
          <button 
            class="btn btn-primary btn-lg start-btn" 
            @click="startTransaction"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isLoading ? 'Loading...' : 'Touch to Start' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KioskWelcome',
  props: {
    config: {
      type: Object,
      default: () => ({
        showLogo: true,
        animationEnabled: true
      })
    },
    transactionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      kioskTitle: 'Welcome to Hercor College',
      welcomeMessage: 'Express Transaction Queueing System',
      showPrinterConfig: false,
      kioskId: localStorage.getItem('kioskId') || '',
      currentKioskId: localStorage.getItem('kioskId') || 'Not set',
      currentPrinterName: '',
      isLoading: true,
      error: null
    };
  },
  async created() {
    // Reset the kiosk state when landing on welcome page
    if (this.$store && this.$store.dispatch) {
      this.$store.dispatch('kiosk/resetKiosk');
    }

    await this.fetchKioskSettings();
    this.updateCurrentPrinterName();
  },
  
  methods: {
    async fetchKioskSettings() {
      this.isLoading = true;
      
      try {
        const timeoutMs = 2000;

        const apiCall = (async () => {
          // Import settingsApi directly
          const { settingsApi } = await import('@/services/api');
          // Fetch kiosk settings
          return settingsApi.getKioskSettings();
        })();

        const timeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Kiosk settings request timed out')), timeoutMs);
        });

        const response = await Promise.race([apiCall, timeout]);

        if (response && response.data) {
          // Update the data properties with the fetched values
          this.kioskTitle = response.data.kiosk_name || this.kioskTitle;
          this.welcomeMessage = response.data.welcome_message || this.welcomeMessage;
          
          // Also save to local storage for offline use
          localStorage.setItem('cachedKioskTitle', this.kioskTitle);
          localStorage.setItem('cachedWelcomeMessage', this.welcomeMessage);
        }
      } catch (error) {
        console.error('Error fetching kiosk settings:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url
        });
        
        // Fallback to cached values if available
        const cachedTitle = localStorage.getItem('cachedKioskTitle');
        const cachedMessage = localStorage.getItem('cachedWelcomeMessage');
        
        // Use cached values or hardcoded defaults
        this.kioskTitle = cachedTitle || 'Welcome to Hercor College';
        this.welcomeMessage = cachedMessage || 'Express Transaction Queueing System';
        
        // Save fallback values to localStorage for next time
        if (!cachedTitle) localStorage.setItem('cachedKioskTitle', this.kioskTitle);
        if (!cachedMessage) localStorage.setItem('cachedWelcomeMessage', this.welcomeMessage);
      } finally {
        this.isLoading = false;
      }
    },
    startTransaction() {
      // Emit step-complete event to move to the next step in the flow
      this.$emit('step-complete', {});
    },
    saveKioskConfig() {
      if (this.kioskId && this.kioskId.trim()) {
        localStorage.setItem('kioskId', this.kioskId.trim());
        this.currentKioskId = this.kioskId.trim();
        
        // Update printer name
        this.updateCurrentPrinterName();
        
        // Show success message
        alert(`Kiosk configuration saved. Using printer: ${this.currentPrinterName}`);
        
        // Close the config panel
        this.showPrinterConfig = false;
      } else {
        alert('Please select a kiosk ID');
      }
    },
    updateCurrentPrinterName() {
      const kioskId = localStorage.getItem('kioskId');
      
      if (kioskId === 'kiosk-1') {
        // Use the exact printer name from wmic output
        this.currentPrinterName = 'XP-58 (Network)';
      } else if (kioskId === 'kiosk-2') {
        // Show network printer display name
        this.currentPrinterName = 'XP-58 2 (Network)';
      } else {
        // Default to RONGTA printer
        this.currentPrinterName = 'RONGTA 58mm Series Printer';
      }
    }
  }
};
</script>

<style scoped>
.welcome-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  text-align: center;
  padding: 2rem;
  position: relative;
  background-color: #f8f9fa;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: 200px;
}

.loading-state .spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.25em;
}

.error-state {
  max-width: 600px;
  margin: 0 auto;
  padding: 1.5rem;
  border-radius: 8px;
  background-color: #fff3cd;
  color: #856404;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-state i {
  font-size: 1.2em;
}

.printer-config-button {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
}

.printer-config-panel {
  position: absolute;
  top: 3.5rem;
  left: 1rem;
  width: 100%;
  max-width: 400px;
  z-index: 20;
  text-align: left;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.welcome-content {
  max-width: 600px;
}

.welcome-title {
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: #2C3E50;
}

.welcome-message {
  font-size: 1.5rem;
  margin-bottom: 3rem;
  color: #555;
}

.welcome-cta {
  margin-top: 2rem;
}

.start-btn {
  font-size: 1.8rem;
  padding: 1rem 3rem;
  border-radius: 50px;
  background-color: #18BC9C;
  border-color: #18BC9C;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.start-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
  background-color: #15a589;
  border-color: #15a589;
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .welcome-title {
    font-size: 3rem;
  }
  
  .welcome-message {
    font-size: 1.8rem;
  }
  
  .start-btn {
    font-size: 2.2rem;
    padding: 1.5rem 4rem;
  }
}
</style> 